import React, { useState } from 'react';
import { Card, Alert, Spinner, Accordion, Table, Button } from 'react-bootstrap';
import { faRedo } from '@fortawesome/free-solid-svg-icons';
import AppBadge from 'shared/badges/app-badge';
import { ActionButton } from 'shared/buttons';
import { useAdminMunicipalityDataUpdates } from 'api/hooks/use-admin-municipality-data-updates';
import PerformancePayloadModal from 'admin/data-pipelines/PerformancePayloadModal';

// While a run is in flight the history is a live view, so poll it. React Query
// only fires the interval while the window/tab has focus (we leave
// refetchIntervalInBackground off), so an idle tab stops hitting the API.
const RUNNING_POLL_MS = 5000;

const isRunning = (record) => record?.status_text === 'running';

// A parent can already read as finished while one of its subtasks is still
// going, so treat either level as "something is running".
const hasRunningUpdate = (updates) =>
  Array.isArray(updates)
  && updates.some((update) => isRunning(update) || (update.subtasks || []).some(isRunning));

const STATUS_VARIANTS = {
  running: 'warning',
  success: 'success',
  error: 'danger',
  timeout: 'timeout'
};

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const getStatusBadge = (status) => (
  <AppBadge variant={STATUS_VARIANTS[status] || 'secondary'}>
    {capitalize(status) || status}
  </AppBadge>
);

const MunicipalityDataUpdates = ({ municipalityId }) => {
  const {
    data: dataUpdates,
    isLoading,
    isFetching,
    error,
    refetch
  } = useAdminMunicipalityDataUpdates({
    municipalityId,
    options: {
      refetchInterval: (updates) => (hasRunningUpdate(updates) ? RUNNING_POLL_MS : false),
      refetchIntervalInBackground: false
    }
  });
  const isPolling = hasRunningUpdate(dataUpdates);
  const [selectedUpdate, setSelectedUpdate] = useState(null);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="text-center p-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      );
    }

    if (error) {
      return (
        <Alert variant="danger" className="mb-0">
          Error loading data updates: {error.message}
        </Alert>
      );
    }

    if (!dataUpdates || dataUpdates.length === 0) {
      return (
        <Alert variant="info" className="mb-0">
          No data updates found for this municipality.
        </Alert>
      );
    }

    return (
      <Accordion>
        {dataUpdates.map((update, index) => (
          <Accordion.Item eventKey={index.toString()} key={update.id}>
            <Accordion.Header>
              <div className="d-flex justify-content-between align-items-center w-100 me-3">
                <div>
                  <strong>{update.function_name}</strong>
                  <small className="text-muted ms-2">{formatDate(update.executed_at)}</small>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted">{formatDuration(update.execution_seconds)}</span>
                  {getStatusBadge(update.status_text)}
                </div>
              </div>
            </Accordion.Header>
            <Accordion.Body>
              <div className="mb-3">
                <dl className="row mb-0">
                  <dt className="col-sm-3">Status:</dt>
                  <dd className="col-sm-9">{getStatusBadge(update.status_text)}</dd>

                  <dt className="col-sm-3">Executed At:</dt>
                  <dd className="col-sm-9">{formatDate(update.executed_at)}</dd>

                  <dt className="col-sm-3">Duration:</dt>
                  <dd className="col-sm-9">{formatDuration(update.execution_seconds)}</dd>

                  {update.message && (
                    <>
                      <dt className="col-sm-3">Message:</dt>
                      <dd className="col-sm-9">
                        <Alert variant={update.status_text === 'error' ? 'danger' : 'info'} className="mb-0">
                          {update.message}
                        </Alert>
                      </dd>
                    </>
                  )}

                  {update.payload && (
                    <>
                      <dt className="col-sm-3">Payload:</dt>
                      <dd className="col-sm-9">
                        <pre className="bg-light p-2 rounded">
                          {JSON.stringify(
                            typeof update.payload === 'string'
                              ? JSON.parse(update.payload)
                              : update.payload,
                            null,
                            2
                          )}
                        </pre>
                      </dd>
                    </>
                  )}
                </dl>
              </div>

              {(update.response_payload || update.aborted_at) && (
                <div className="mb-3">
                  <Button
                    variant="light"
                    className="text-primary"
                    size="sm"
                    onClick={() => setSelectedUpdate(update)}
                  >
                    View performance details
                  </Button>
                </div>
              )}

              {update.subtasks && update.subtasks.length > 0 && (
                <div>
                  <h6 className="mb-3">Subtasks ({update.subtasks.length})</h6>
                  <Table bordered size="sm">
                    <thead>
                      <tr>
                        <th>Function</th>
                        <th>Executed At</th>
                        <th>Duration</th>
                        <th>Status</th>
                        <th>Message</th>
                        <th style={{ width: '6rem' }} />
                      </tr>
                    </thead>
                    <tbody>
                      {update.subtasks.map((subtask) => (
                        <tr key={subtask.id}>
                          <td>{subtask.function_name}</td>
                          <td>{formatDate(subtask.executed_at)}</td>
                          <td>{formatDuration(subtask.execution_seconds)}</td>
                          <td>{getStatusBadge(subtask.status_text)}</td>
                          <td>
                            {subtask.message && (
                              <small className={subtask.status_text === 'error' ? 'text-danger' : ''}>
                                {subtask.message}
                              </small>
                            )}
                          </td>
                          {/* The importers run as subtasks, so their own breakdown — per-step
                              timings, the row account — only reaches the UI from here. */}
                          <td>
                            {(subtask.response_payload || subtask.aborted_at) && (
                              <Button
                                variant="light"
                                className="text-primary"
                                size="sm"
                                onClick={() => setSelectedUpdate(subtask)}
                              >
                                Details
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>
    );
  };

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div>
          <h5 className="mb-0">Data Updates History</h5>
          <small className="text-muted">
            Showing the 50 most recent updates
            {isPolling && ' — auto-refreshing every 5s while updates are running'}
          </small>
        </div>
        <ActionButton
          type="button"
          variant="light-primary"
          icon={faRedo}
          label="Refresh"
          loadingLabel="Refreshing"
          // Only the manual fetches get the spinner — flipping the button
          // every 5s while polling would just read as a flicker.
          loading={isFetching && !isPolling}
          onClick={() => refetch()}
        />
      </Card.Header>
      <Card.Body>
        {renderBody()}
      </Card.Body>

      <PerformancePayloadModal
        show={!!selectedUpdate}
        onHide={() => setSelectedUpdate(null)}
        dataUpdate={selectedUpdate}
      />
    </Card>
  );
};

export default MunicipalityDataUpdates;
